import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { toast } from "react-toastify";
import { useAdminContext } from "../../contexts/AdminContext";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import Loader from "../../utils/Loader";

const LogComment = ({ userId, logType, logFirebaseId, currentUser }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(false);
  const { selectedUserId, isViewingOtherUser } = useAdminContext();
  const currentUserId = isViewingOtherUser ? selectedUserId : userId;
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [deleteCommentId, setDeleteCommentId] = useState(null);
  const [confirmationText, setConfirmationText] = useState("");

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
    ],
  };

  // Map log types to collection names
  const getCollectionName = (type) => {
    const mapping = {
      "secretarial-records": "secretaryLogs",
      "process-server-logs": "serverLogs",
      "exec-admin": "execAdmin",
      "case-brief": "caseBrief",
    };
    return mapping[type] || type;
  };

  // Real-time listener for comments
  useEffect(() => {
    if (!currentUserId || !logFirebaseId) return;

    const collectionName = getCollectionName(logType);
    const commentsRef = collection(
      db,
      "logs",
      currentUserId,
      collectionName,
      logFirebaseId,
      "logComments"
    );
    const q = query(commentsRef, orderBy("createdAt", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const loadedComments = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setComments(loadedComments);
      },
      (error) => {
        console.error("Error loading comments:", error);
        toast.error("Failed to load comments");
      }
    );

    return () => unsubscribe();
  }, [currentUserId, logType, logFirebaseId]);

  const handleAddComment = async () => {
    if (!newComment.trim() || newComment === "<p><br></p>") {
      toast.warning("Please enter a comment");
      return;
    }

    try {
      setLoading(true);
      setLoadingText("Saving Comment...");
      const collectionName = getCollectionName(logType);
      const commentsRef = collection(
        db,
        "logs",
        currentUserId,
        collectionName,
        logFirebaseId,
        "logComments"
      );

      await addDoc(commentsRef, {
        content: newComment,
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email,
        createdByRole: currentUser.role || "user",
      });

      setNewComment("");
      toast.success("Comment added");
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editContent.trim() || editContent === "<p><br></p>") {
      toast.warning("Comment cannot be empty");
      return;
    }

    try {
      setLoading(true);
      setLoadingText("Updating Comment...");
      const collectionName = getCollectionName(logType);
      const commentRef = doc(
        db,
        "logs",
        currentUserId,
        collectionName,
        logFirebaseId,
        "logComments",
        commentId
      );

      await updateDoc(commentRef, {
        content: editContent,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid,
      });

      setEditingId(null);
      setEditContent("");
      toast.success("Comment updated");
    } catch (error) {
      console.error("Error updating comment:", error);
      toast.error("Failed to update comment");
    } finally {
      setLoading(false);

      setLoadingText("");
    }
  };

  const requestDelete = (commentId) => {
    setDeleteCommentId(commentId);
    setConfirmationText(`Are you sure you want to delete this comment?`);
    setConfirmDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteModalOpen(false);
    setDeleteCommentId(null);
  };

  const handleDeleteComment = async () => {
    if (!deleteCommentId) {
      toast.warn("Failed to delete comment.");
      return;
    }

    try {
      setLoading(true);
      setLoadingText("Deleting Comment...");
      const collectionName = getCollectionName(logType);
      const commentRef = doc(
        db,
        "logs",
        currentUserId,
        collectionName,
        logFirebaseId,
        "logComments",
        deleteCommentId
      );

      await deleteDoc(commentRef);
      toast.success("Comment deleted");
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment");
    } finally {
      setLoading(false);
      setLoadingText("");
      setConfirmDeleteModalOpen(false);
      setDeleteCommentId(null);
    }
  };

  const canEditOrDelete = (comment) => {
    return comment.createdBy === currentUser.uid;
  };

  if (!logFirebaseId) {
    return (
      <div className="form-section max-w-6xl w-full">
        <div className="text-center py-8 text-alt-color">
          <span className="material-symbols-outlined text-4xl opacity-50">
            chat_bubble_outline
          </span>
          <p className="mt-2">Save the log first to enable comments</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmDeleteModal
        isOpen={confirmDeleteModalOpen}
        onConfirm={handleDeleteComment}
        onCancel={handleCancelDelete}
        title="Confirm Delete"
        message={confirmationText}
      />

      {loading && <Loader text={loadingText} />}

      <div className="form-section max-w-6xl w-full">
        <h3 className="text-lg font-semibold text-color mb-4 flex items-center gap-2">
          <span className="material-symbols-outlined">forum</span>
          Comments & Discussion
        </h3>

        {/* Add new comment */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-color mb-2">
            Add Comment
          </label>
          <ReactQuill
            value={newComment}
            onChange={setNewComment}
            modules={quillModules}
            placeholder="Write your comment..."
            theme="snow"
            className="bg-white/10 mb-2"
          />
          <button
            onClick={handleAddComment}
            disabled={loading}
            className="btn-action"
          >
            Comment
          </button>
        </div>

        {/* Comments list */}
        <div className="space-y-4">
          {comments.length === 0 ? (
            <div className="text-center py-8 text-alt-color">
              <span className="material-symbols-outlined text-4xl opacity-50">
                chat_bubble_outline
              </span>
              <p className="mt-2">No comments yet. Be the first to comment!</p>
            </div>
          ) : (
            comments.map((comment) => (
              <div
                key={comment.id}
                className="bg-white/5 border border-gray-300 dark:border-gray-600 rounded-lg p-4"
              >
                {/* Comment header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-tier-secondary flex items-center justify-center text-white text-sm font-semibold">
                      {comment.createdByName?.charAt(0).toUpperCase() || "U"}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-color">
                        {comment.createdByName}
                        {comment.createdByRole === "admin" && (
                          <span className="ml-2 text-xs px-2 py-0.5 bg-blue-500/20 text-blue-500 rounded">
                            Admin
                          </span>
                        )}
                        {comment.createdByRole === "superadmin" && (
                          <span className="ml-2 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-500 rounded">
                            Super Admin
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-alt-color">
                        {new Date(comment.createdAt).toLocaleString()}
                        {comment.updatedAt && " (edited)"}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  {canEditOrDelete(comment) && (
                    <div className="flex gap-1">
                      {editingId !== comment.id && (
                        <>
                          <button
                            onClick={() => {
                              setEditingId(comment.id);
                              setEditContent(comment.content);
                            }}
                            className="p-1 hover:bg-blue-500/20 rounded transition-colors"
                            title="Edit"
                          >
                            <span className="material-symbols-outlined text-sm text-blue-500">
                              edit
                            </span>
                          </button>
                          <button
                            onClick={() => requestDelete(comment.id)}
                            className="p-1 hover:bg-red-500/20 rounded transition-colors"
                            title="Delete"
                          >
                            <span className="material-symbols-outlined text-sm text-red-500">
                              delete
                            </span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Comment content */}
                {editingId === comment.id ? (
                  <div>
                    <ReactQuill
                      value={editContent}
                      onChange={setEditContent}
                      modules={quillModules}
                      theme="snow"
                      className="bg-white/10 mb-2"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditComment(comment.id)}
                        disabled={loading}
                        className="btn-action text-xs"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingId(null);
                          setEditContent("");
                        }}
                        className="btn-action-white text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="text-sm text-color comment-content"
                    dangerouslySetInnerHTML={{ __html: comment.content }}
                  />
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default LogComment;
